import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ConfirmationModal,
  EmptyState,
  LoadingState,
  MobileSearchField,
  Modal,
  ModalCloseButton,
  ModalContent,
  PlayerSearchInput,
  StatusBadge,
} from '../common';
import { Heading, Text } from '../common/Typography';
import { FormField, Input } from '../common/Form';
import { formatPlayerName } from '../../utils/nameUtils';
import { getGenderTextColor } from '../../utils/genderColors';
import { getPlayerRosterTypeMeta } from '../../utils/clubPlayerLinkDedup';
import { playerMatchesSearchQuery } from '../../utils/playerUtils';
import {
  createPasteAliasAdmin,
  deletePasteAliasAdmin,
  fetchClubPlayersForPasteAliases,
  fetchPasteAliasRowsByClub,
  updatePasteAliasAdmin,
} from '../../services/clubPlayerPasteAliasService';

function playerLabel(player) {
  return formatPlayerName(player) || 'Unknown player';
}

function AliasPlayerMeta({ player }) {
  const typeMeta = getPlayerRosterTypeMeta(player);
  return (
    <div className="min-w-0">
      <div className={`truncate font-medium ${getGenderTextColor(player?.gender)}`}>
        {playerLabel(player)}
      </div>
      {player?.is_guest ? (
        <StatusBadge variant={typeMeta.variant} size="small" className="mt-1">
          {typeMeta.label}
        </StatusBadge>
      ) : null}
    </div>
  );
}

function PasteAliasFormModal({
  isOpen,
  onClose,
  clubId,
  players,
  initialAlias = null,
  lockedPlayer = null,
  onSaved,
}) {
  const [aliasText, setAliasText] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setAliasText(initialAlias?.alias_display || '');
    setSelectedPlayer(lockedPlayer || initialAlias?.player || null);
    setError(null);
    setSaving(false);
  }, [isOpen, initialAlias, lockedPlayer]);

  const handleSave = async (event) => {
    event?.preventDefault?.();
    if (saving) return;
    if (!selectedPlayer?.id) {
      setError('Choose a club player.');
      return;
    }
    setSaving(true);
    setError(null);
    const result = initialAlias
      ? await updatePasteAliasAdmin({
          clubId,
          aliasId: initialAlias.id,
          player: selectedPlayer,
          aliasText,
        })
      : await createPasteAliasAdmin({
          clubId,
          player: selectedPlayer,
          aliasText,
        });
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Could not save nickname.');
      return;
    }
    onSaved?.(result.row);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen onClose={onClose} closeOnBackdropClick={!saving} className="z-[60]">
      <ModalContent size="medium" padding={false} constrainHeight>
        <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border-light px-4 py-3 sm:px-5">
            <div className="min-w-0 pr-8">
              <Heading level={2} className="text-lg font-semibold text-text-primary">
                {initialAlias ? 'Edit spreadsheet name' : 'Add spreadsheet name'}
              </Heading>
              <Text className="mt-0.5 text-sm text-text-secondary">
                Used when a host pastes a player list. One nickname maps to one player in this club.
              </Text>
            </div>
            <ModalCloseButton onClose={onClose} className="static shrink-0" disabled={saving} />
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
            {error ? (
              <p className="rounded-md border border-danger/30 bg-danger-light px-3 py-2 text-sm text-danger-dark" role="alert">
                {error}
              </p>
            ) : null}
            <FormField label="Nickname" required helperText="As it appears on the spreadsheet, at least 2 characters.">
              <Input
                value={aliasText}
                onChange={(e) => setAliasText(e.target.value)}
                placeholder="e.g. Tony"
                disabled={saving}
                aria-label="Spreadsheet nickname"
                autoComplete="off"
              />
            </FormField>
            <FormField label="Player" required>
              {lockedPlayer ? (
                <AliasPlayerMeta player={lockedPlayer} />
              ) : (
                <PlayerSearchInput
                  placeholder="Search club players…"
                  availablePlayers={players}
                  selectedPlayer={selectedPlayer}
                  onSelectPlayer={setSelectedPlayer}
                  onRemovePlayer={() => setSelectedPlayer(null)}
                  showSelectedInfo
                />
              )}
            </FormField>
          </div>
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border-light px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
            <Button type="button" variant="outline" size="small" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="small" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
}

export function PasteAliasManager({ clubId, className = '' }) {
  const [rows, setRows] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    setError(null);
    const [aliasResult, playerResult] = await Promise.all([
      fetchPasteAliasRowsByClub(clubId),
      fetchClubPlayersForPasteAliases(clubId),
    ]);
    if (!aliasResult.success) setError(aliasResult.error || 'Failed to load spreadsheet names.');
    if (!playerResult.success && !aliasResult.success) {
      setError(playerResult.error || aliasResult.error || 'Failed to load spreadsheet names.');
    }
    setRows(aliasResult.rows || []);
    setPlayers(playerResult.players || []);
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const term = search.trim();
    if (!term) return rows;
    return rows.filter((row) => {
      if (String(row.alias_display || '').toLowerCase().includes(term.toLowerCase())) return true;
      if (String(row.alias_normalized || '').includes(term.toLowerCase())) return true;
      return playerMatchesSearchQuery(row.player, term);
    });
  }, [rows, search]);

  const handleDelete = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    const result = await deletePasteAliasAdmin({ clubId, aliasId: pendingDelete.id });
    setDeleting(false);
    if (!result.success) {
      setError(result.error || 'Could not remove nickname.');
      return;
    }
    setRows((prev) => prev.filter((row) => row.id !== pendingDelete.id));
    setPendingDelete(null);
  };

  return (
    <div className={className}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <Heading level={2} className="text-xl font-bold text-text-primary sm:text-2xl">
            Spreadsheet names
          </Heading>
          <Text className="mt-1 text-sm text-text-secondary">
            Nicknames learned from paste lists. Hosts and admins can add, reassign, or remove them. Separate from bank-name hints on payments.
          </Text>
        </div>
        <Button
          variant="primary"
          size="small"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          disabled={!clubId || loading}
        >
          Add nickname
        </Button>
      </div>

      {error ? (
        <p className="mb-4 text-sm text-danger-dark" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && rows.length > 0 ? (
        <div className="mb-4">
          <MobileSearchField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search nickname or player…"
            showIcon
            aria-label="Search spreadsheet names"
          />
        </div>
      ) : null}

      {loading ? (
        <LoadingState message="Loading spreadsheet names…" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No spreadsheet names yet"
          description="When a host chooses a player for a pasted nickname, it is stored here. You can also add one now."
          action={
            <Button
              variant="primary"
              size="small"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              Add nickname
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No matching names"
          description={`Nothing matches “${search.trim()}”.`}
        />
      ) : (
        <>
          <ul className="space-y-3 lg:hidden">
            {filtered.map((row) => (
              <li key={row.id} className="rounded-lg border border-border-medium bg-surface p-3">
                <div className="font-medium text-text-primary">{row.alias_display}</div>
                <div className="mt-2">
                  <AliasPlayerMeta player={row.player} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => {
                      setEditing(row);
                      setFormOpen(true);
                    }}
                  >
                    Change
                  </Button>
                  <Button variant="ghost" size="small" onClick={() => setPendingDelete(row)}>
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto lg:block">
            <table className="min-w-full divide-y divide-border-medium">
              <thead>
                <tr className="bg-surface-subtle">
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Nickname
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Player
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 text-sm font-medium text-text-primary">{row.alias_display}</td>
                    <td className="px-4 py-2">
                      <AliasPlayerMeta player={row.player} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() => {
                            setEditing(row);
                            setFormOpen(true);
                          }}
                        >
                          Change
                        </Button>
                        <Button variant="ghost" size="small" onClick={() => setPendingDelete(row)}>
                          Remove
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <PasteAliasFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        clubId={clubId}
        players={players}
        initialAlias={editing}
        onSaved={() => load()}
      />

      <ConfirmationModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Remove spreadsheet name?"
        message={`Paste lists will no longer auto-match “${pendingDelete?.alias_display || ''}”. You can add it again later.`}
        confirmText={deleting ? 'Removing…' : 'Remove'}
        cancelText="Keep"
        isDangerous
        loading={deleting}
      />
    </div>
  );
}

export default PasteAliasManager;
